import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import * as Icons from "react-icons/bs";
import AdvancedSearchBoxNew from "../../../fragments/AdvancedSearchBoxNew";
import { OptionType, Themes, TWindowPosition } from "../../types";
import { UserContext } from "../../../context";
import { SelectPanel, SelectPanelProps } from "./SelectPanel";
import { SelectAndTextPanel, SelectAndTextPanelProps } from "./SelectAndTextPanel";
import { NumberPanel, NumberPanelProps } from "./NumberPanel";
import "../../scss/advanced-search.scss";
import "../modal/Modal.css";
import useDraggable from "../../hooks/useDraggable";
import { useDocuments } from "../../contexts/DekstopContext";
import { SearchContext } from "../../../contexts/SearchContext";

export type AdvancedSearchProps = {
  theme: Themes;
  onClose: () => void;
};

const AdvancedSearchBoxElectronWrapper = ({ onClose }: { onClose: () => void }) => {
  const { searchParams } = useContext(SearchContext);
  const { searchWithFilters } = useDocuments();
  const [shouldSearch, setShouldSearch] = useState(false);

  useEffect(() => {
    if (shouldSearch) {
      setShouldSearch(false);
      searchWithFilters(searchParams as Record<string, any>);
    }
  }, [shouldSearch, searchParams, searchWithFilters]);

  return <AdvancedSearchBoxNew onHide={onClose} onSearch={() => setShouldSearch(true)} />;
};

export const AdvancedSearch = (props: AdvancedSearchProps) => {
  const someRef = useRef<HTMLDivElement>(null); // should be removed
  if (props.theme === Themes.VINTAGE) return <AdvancedSearchVintage {...props} containerRef={someRef} />;

  return <AdvancedSearchBoxElectronWrapper onClose={props.onClose} />;
};

const title = "Cautare acte";

const AdvancedSearchVintage = ({
  theme,
  onClose,
  containerRef,
}: AdvancedSearchProps & { containerRef: React.RefObject<HTMLDivElement | null> }) => {
  const { searchAggs } = useContext(UserContext);

  const ref = useRef<HTMLDivElement>(null);
  const [isOverflow, setIsOverflow] = useState(false);

  const [position, setPosition] = useState<TWindowPosition>({ x: 0, y: 0 });
  const { searchProps, setSearchProps, clearSearchProps, onSearch } = useDocuments();

  const windowRef = useRef<HTMLDivElement>(null);

  const { isDragging, handleMouseDown } = useDraggable({
    initialPosition: position,
    containerRef,
    elementRef: windowRef,
    onPositionChange: (p) => setPosition(p),
    autoAlignToMiddle: true,
  });

  const panels: (
    | { type: "select"; data: SelectPanelProps }
    | { type: "select-and-text"; data: SelectAndTextPanelProps }
    | { type: "numbers"; data: NumberPanelProps }
  )[] = useMemo(
    () => [
      {
        type: "numbers",
        data: {
          title: "Emitere",
          doc_nr: searchProps.doc_nr,
          doc_year: searchProps.doc_year,
          mof_nr: searchProps.mof_nr,
          mof_year: searchProps.mof_year,
          onChange: (data) => setSearchProps((prev) => ({ ...prev, ...data })),
        },
      },
      {
        type: "select",
        data: {
          title: "Emitent",
          options: (searchAggs as any).issuers.map((i: string) => ({ value: i, label: i })),
          values: searchProps.issuers,
          onChange: (newValue: OptionType[]) => setSearchProps((prev) => ({ ...prev, issuers: newValue })),
        },
      },
      {
        type: "select-and-text",
        data: {
          title: "Cuvinte",
          options: [
            { value: "titlu", label: "In titlu" },
            { value: "text", label: "In text" },
          ],
          selectValue: searchProps.searchTerms.type,
          textValues: searchProps.searchTerms.terms,
          onChange: (selectValue: OptionType, textValues: string[]) =>
            setSearchProps((prev) => ({
              ...prev,
              searchTerms: {
                terms: textValues,
                type: selectValue,
              },
            })),
        },
      },
      {
        type: "select",
        data: {
          title: "Domeniu",
          options: (searchAggs as any).domains.map((i: string) => ({ value: i, label: i })),
          values: searchProps.domains,
          onChange: (newValue: OptionType[]) => setSearchProps((prev) => ({ ...prev, domains: newValue })),
        },
      },
      {
        type: "select",
        data: {
          title: "Tipul actului",
          options: (searchAggs as any).types.map((i: string) => ({ value: i, label: i })),
          values: searchProps.types,
          onChange: (newValue: OptionType[]) => setSearchProps((prev) => ({ ...prev, types: newValue })),
        },
      },
      {
        type: "select",
        data: {
          title: "Starea actului",
          options: (searchAggs as any).status_names.map((i: string) => ({ value: i, label: i })),
          values: searchProps.state,
          onChange: (newValue: OptionType[]) => setSearchProps((prev) => ({ ...prev, state: newValue })),
        },
      },
    ],
    [searchProps, searchAggs, setSearchProps]
  );

  useEffect(() => {
    const handler = (ev: KeyboardEvent) => {
      if (ev.key === "Enter") {
        onSearch();
        onClose();
        clearSearchProps();
      }
    }
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [clearSearchProps, onClose, onSearch]);

  // check if overflowing to increase width and make room for scrollbar;
  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const checkOverflow = () => {
      const overflowing = el.scrollWidth > el.clientWidth || el.scrollHeight > el.clientHeight;
      setIsOverflow(overflowing);
    };

    // Run on mount
    checkOverflow();

    const ro = new ResizeObserver(checkOverflow);
    ro.observe(el);
    window.addEventListener("resize", checkOverflow);

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", checkOverflow);
    };
  }, []);

  return (
    <div
      className={`virtual-window advanced-search-container  ${isDragging ? "dragging" : ""} ${theme}`}
      ref={windowRef}
      style={{
        position: "absolute",
        top: `${position.y}px`,
        left: `${position.x}px`,
      }}
    >
      <div className="window-header" onMouseDown={handleMouseDown}>
        <div className="window-title">
          <i className="file-icon">
            <Icons.BsSearch />
          </i>
          <span>{title}</span>
        </div>
        <div className="window-controls">
          <button
            className="window-control close"
            onClick={() => {
              onClose();
              clearSearchProps();
            }}
          >
            ✕
          </button>
        </div>
      </div>
      <div ref={ref} className={`window-content ${isOverflow ? "overflowing" : ""}`}>
        {panels.map((panel) =>
          panel.type === "select" ? (
            <SelectPanel key={panel.data.title} {...panel.data} />
          ) : panel.type === "select-and-text" ? (
            <SelectAndTextPanel key={panel.data.title} {...panel.data} />
          ) : (
            <NumberPanel key={panel.data.title} {...panel.data} />
          )
        )}
      </div>
      <div className="window-footer">
        <div className="advanced-search-button" style={{ opacity: 0 }}></div>
        <button
          className="advanced-search-button"
          onClick={() => {
            onSearch();
            onClose();
            clearSearchProps();
          }}
        >
          Cauta
        </button>
        <button
          className="advanced-search-button"
          onClick={() => {
            onClose();
            clearSearchProps();
          }}
        >
          Renunta
        </button>
        <button className="advanced-search-button">Ajutor</button>
      </div>
    </div>
  );
};

export const AdvancedSearchVintageFullModal = ({ onClose }: { onClose: () => void }) => {
  const containerRef = useRef<HTMLDivElement>(null);

  return (
    <div className="full-screen-modal" ref={containerRef}>
      <AdvancedSearchVintage containerRef={containerRef} theme={Themes.VINTAGE} onClose={onClose} />
    </div>
  );
};
